/**
 * Survives Home ↔ QcsHome remounts so CareBrain / QCS scope badges and
 * overview do not flash 0 while /bot/list is in flight.
 *
 * sessionStorage is best-effort; an in-memory copy covers same-tab remounts
 * when storage is unavailable.
 */

const STORAGE_KEY = 'orgPlatformCounts'

let memory = null

const isValid = value =>
  value && typeof value.care === 'number' && typeof value.qcs === 'number'

export const readPlatformCounts = () => {
  if (isValid(memory)) return memory

  try {
    const raw = window.sessionStorage.getItem(STORAGE_KEY)
    if (!raw) return null
    const parsed = JSON.parse(raw)
    if (!isValid(parsed)) return null
    memory = { care: parsed.care, qcs: parsed.qcs }
    return memory
  } catch {
    return null
  }
}

/** Merge partial totals and persist. Returns the stored shape. */
export const writePlatformCounts = partial => {
  const prev = readPlatformCounts() || { care: 0, qcs: 0 }
  const next = {
    care: typeof partial?.care === 'number' ? partial.care : prev.care,
    qcs: typeof partial?.qcs === 'number' ? partial.qcs : prev.qcs,
  }
  memory = next
  try {
    window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(next))
  } catch {
    // Preference only — private mode must not break the list page.
  }
  return next
}

/**
 * Call when constructing Home / QcsHome state (per remount), not once at
 * module load — App imports both routes early, before any list has written.
 */
export const getInitialPlatformCountState = () => {
  const seeded = readPlatformCounts()
  return {
    careCount: seeded?.care ?? 0,
    qcsCount: seeded?.qcs ?? 0,
    careCountFetched: Boolean(seeded),
    qcsCountFetched: Boolean(seeded),
    countsReady: Boolean(seeded),
  }
}
